import { useCallback, useEffect, useRef, useState } from "react";
import { Video, SwitchCamera, RotateCcw, Check } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const MAX = 10;

function pickMime() {
  const opts = ["video/mp4;codecs=avc1,mp4a", "video/mp4", "video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"];
  return opts.find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
}

// Record a ≤10s clip with the device camera (3-2-1 countdown, auto-stop). onUse(file, seconds).
export default function CameraClipModal({ open, onClose, onUse }) {
  const liveRef = useRef(null);
  const streamRef = useRef(null);
  const recRef = useRef(null);
  const timers = useRef([]);
  const [facing, setFacing] = useState("environment");
  const [phase, setPhase] = useState("idle"); // idle | countdown | recording | review
  const [count, setCount] = useState(3);
  const [elapsed, setElapsed] = useState(0);
  const [take, setTake] = useState(null); // { url, file, secs }

  const clearTimers = () => { timers.current.forEach(clearInterval); timers.current.forEach(clearTimeout); timers.current = []; };
  const stopStream = () => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; };

  const startStream = useCallback(async (face) => {
    stopStream();
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: face, width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } }, audio: true,
      });
      streamRef.current = s;
      if (liveRef.current) { liveRef.current.srcObject = s; liveRef.current.play().catch(() => {}); }
    } catch {
      toast.error("Couldn't open the camera — allow camera & mic access and try again");
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (open) { setPhase("idle"); setTake(null); startStream(facing); }
    return () => { clearTimers(); stopStream(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const flip = () => { const f = facing === "environment" ? "user" : "environment"; setFacing(f); startStream(f); };

  const record = () => {
    const s = streamRef.current;
    if (!s) return;
    const mime = pickMime();
    const chunks = [];
    const rec = new MediaRecorder(s, { ...(mime ? { mimeType: mime } : {}), videoBitsPerSecond: 2_500_000, audioBitsPerSecond: 96_000 });
    recRef.current = rec;
    const t0 = { v: 0 };
    rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    rec.onstop = () => {
      clearTimers();
      const type = (rec.mimeType || mime || "video/webm").split(";")[0];
      const blob = new Blob(chunks, { type });
      const secs = Math.min(MAX, (Date.now() - t0.v) / 1000);
      const file = new File([blob], `clip-${Date.now()}.${type.includes("mp4") ? "mp4" : "webm"}`, { type });
      setTake({ url: URL.createObjectURL(blob), file, secs });
      setPhase("review");
    };
    t0.v = Date.now();
    rec.start(250);
    setPhase("recording"); setElapsed(0);
    timers.current.push(setInterval(() => setElapsed(Math.min(MAX, (Date.now() - t0.v) / 1000)), 100));
    timers.current.push(setTimeout(() => rec.state !== "inactive" && rec.stop(), MAX * 1000));
  };

  const go = () => {
    setPhase("countdown"); setCount(3);
    let n = 3;
    const iv = setInterval(() => {
      n -= 1;
      if (n <= 0) { clearInterval(iv); record(); } else setCount(n);
    }, 1000);
    timers.current.push(iv);
  };
  const stop = () => recRef.current?.state === "recording" && recRef.current.stop();
  const retake = () => { if (take?.url) URL.revokeObjectURL(take.url); setTake(null); setPhase("idle"); };
  const use = () => { if (take) { onUse(take.file, take.secs); onClose(); } };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f0f16] border-2 border-[#5d8a52] text-white max-w-lg" data-testid="camera-clip-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#f0c975] text-lg font-extrabold"><Video size={18} /> Record a 10-second clip</DialogTitle>
          <DialogDescription className="text-white/70">Hold steady — recording stops automatically at 10 seconds.</DialogDescription>
        </DialogHeader>
        <div className="relative rounded-xl overflow-hidden bg-black aspect-video">
          <video ref={liveRef} muted playsInline className={`w-full h-full object-cover ${phase === "review" ? "hidden" : ""} ${facing === "user" ? "-scale-x-100" : ""}`} data-testid="camera-clip-live" />
          {phase === "review" && take && <video src={take.url} controls playsInline className="w-full h-full object-contain" data-testid="camera-clip-review" />}
          {phase === "countdown" && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40" data-testid="camera-clip-countdown">
              <span key={count} className="text-7xl font-extrabold text-white sms-fade-up">{count}</span>
            </div>
          )}
          {phase === "recording" && (
            <span className="absolute top-2 left-2 inline-flex items-center gap-1.5 rounded-full bg-black/70 px-2.5 py-1 text-xs font-extrabold" data-testid="camera-clip-timer">
              <span className="w-2 h-2 rounded-full bg-[#ef4444] animate-pulse" /> 0:{String(Math.floor(elapsed)).padStart(2, "0")} / 0:10
            </span>
          )}
          {(phase === "idle" || phase === "countdown") && (
            <button type="button" onClick={flip} disabled={phase !== "idle"} data-testid="camera-clip-flip" title="Flip camera"
              className="absolute top-2 right-2 w-9 h-9 rounded-full bg-black/70 flex items-center justify-center hover:bg-black/90 disabled:opacity-40 transition-colors">
              <SwitchCamera size={17} />
            </button>
          )}
        </div>
        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden" data-testid="camera-clip-progress">
          <div className="h-full rounded-full transition-[width] duration-100 ease-linear" style={{ width: `${(phase === "review" ? (take?.secs || 0) : elapsed) / MAX * 100}%`, background: "linear-gradient(90deg,#f0c975,#ef4444)" }} />
        </div>
        {phase === "review" ? (
          <div className="flex gap-2">
            <button type="button" onClick={retake} data-testid="camera-clip-retake" className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-[#5d8a52] py-2.5 font-extrabold hover:bg-white/10 transition-colors"><RotateCcw size={16} /> Retake</button>
            <button type="button" onClick={use} data-testid="camera-clip-use" className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#dc2626] hover:bg-[#b91c1c] py-2.5 font-extrabold transition-colors"><Check size={16} /> Use clip</button>
          </div>
        ) : (
          <div className="flex justify-center">
            <button type="button" onClick={phase === "recording" ? stop : go} disabled={phase === "countdown"} data-testid={phase === "recording" ? "camera-clip-stop" : "camera-clip-record"}
              className="w-16 h-16 rounded-full border-4 border-white flex items-center justify-center active:scale-95 transition-transform disabled:opacity-50">
              <span className={`bg-[#ef4444] transition-all ${phase === "recording" ? "w-6 h-6 rounded-md" : "w-11 h-11 rounded-full"}`} />
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
